import { useCallback, useEffect, useState } from "react";
import { adminService } from "../../features/admin/service";
import type {
  CdnRedisOverviewResponse,
  CdnQueueStatusResponse,
  RedisNamespacesResponse,
  RedisClientsResponse,
} from "../../features/admin/types";
import Spinner from "../../components/ui/Spinner";
import InfoTooltip from "../../components/common/InfoTooltip";
import RefreshButton from "../../components/admin/RefreshButton";
import { Card, Warnings, num } from "./AdminRealtimeOpsPanels";

/**
 * Admin ▸ Settings ▸ CDN Redis Health.
 *
 * Sibling of AdminRecRedisHealthPage/AdminWebSocketHealthPage, scoped entirely
 * to the storage-migration queue's own dedicated Redis (CDN_REDIS_URL) — a
 * genuinely separate CONTAINER from the shared backend/RAG/OCR instance
 * (WebSocket Health tab) and from rec_engine's own instance (Rec Redis Health
 * tab), not just another logical DB on either.
 * Smaller surface than rec's page on purpose: this Redis holds exactly one
 * plain LIST (see app.modules.admin.storage_migration_queue), not scheduled
 * jobs or a separate process's sockets, so there's no jobs/realtime tab here —
 * one page: instance health, the queue itself, namespaces, live connections.
 */

const REFRESH_INTERVAL_MS = 5000;

function Th({ children, hint, className }: { children: React.ReactNode; hint: string; className?: string }) {
  return (
    <th className={className ?? ""}>
      <span className="inline-flex items-center">
        {children}
        <InfoTooltip text={hint} />
      </span>
    </th>
  );
}

const STATUS_TONE: Record<string, string> = {
  completed: "text-emerald-400",
  in_progress: "text-indigo-300",
  queued: "text-amber-400",
  failed: "text-red-400",
};

const AdminCdnRedisHealthPage: React.FC = () => {
  const [ov, setOv] = useState<CdnRedisOverviewResponse | null>(null);
  const [queue, setQueue] = useState<CdnQueueStatusResponse | null>(null);
  const [ns, setNs] = useState<RedisNamespacesResponse | null>(null);
  const [cl, setCl] = useState<RedisClientsResponse | null>(null);
  const [match, setMatch] = useState("storage_migration:*");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [o, q, c] = await Promise.all([
        adminService.getCdnRedisOverview(),
        adminService.getCdnQueueStatus(),
        adminService.getCdnRedisClients(),
      ]);
      setOv(o);
      setQueue(q);
      setCl(c);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load CDN Redis overview");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadNs = useCallback(async (pattern: string) => {
    try {
      setNs(await adminService.getCdnRedisNamespaces(pattern));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Namespace scan failed");
    }
  }, []);

  useEffect(() => {
    load();
    loadNs(match);
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, loadNs]);

  if (loading) return <Spinner />;
  if (err) return <p className="text-sm text-red-400">{err}</p>;

  if (!ov?.enabled) {
    return (
      <div>
        <Warnings
          items={[
            ov?.reason ??
              "CDN Redis unreachable — CDN_REDIS_URL unset or the instance is down.",
          ]}
        />
        <p className="text-xs text-slate-500 mt-2">
          The storage-migration queue degrades gracefully without this: every job's real
          status/progress lives in Mongo regardless, and the worker's periodic stale-sweep
          recovers anything stuck — it's just slower than an instant Redis pop.
          Configured pool size: {num(ov?.configured_pool_size ?? null)}.
        </p>
      </div>
    );
  }

  const m = ov.memory, c = ov.clients, st = ov.stats;
  const cap = c?.maxclients || ov.configured_pool_size || 0;
  const connPct = cap ? ((c?.connected ?? 0) / cap) * 100 : 0;
  const statusCounts = queue?.mongo_status_counts ?? {};

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-white">CDN Redis — storage migration queue</h2>
          <p className="text-sm text-gray-500 mt-1">
            Dedicated Redis (CDN_REDIS_URL) behind the Storage Routing tab's "Migrate
            existing objects" — separate from the shared backend/RAG/OCR Redis and from
            rec_engine's own instance.
          </p>
        </div>
        <RefreshButton onRefresh={async () => { await Promise.all([load(), loadNs(match)]); }} />
      </div>

      <Warnings items={ov.warnings} />

      {/* The queue itself — what a superadmin actually wants to know */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-slate-200 mb-2">Queue</h3>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-5 mb-2">
          <Card
            label="Waiting to be picked up"
            value={num(queue?.queue_length ?? null)}
            tone={(queue?.queue_length ?? 0) > 0 ? "warn" : "ok"}
            hint="Job ids currently sitting in the Redis dispatch list, not yet BLPOP'd by a worker. This is dispatch signal only — a job's real status/progress always lives in Mongo (see the counts to the right)."
          />
          <Card
            label="Queued"
            value={num(statusCounts.queued ?? 0)}
            tone={(statusCounts.queued ?? 0) > 0 ? "warn" : "ok"}
            hint="Mongo job documents with status=queued right now, across every domain."
          />
          <Card
            label="In progress"
            value={num(statusCounts.in_progress ?? 0)}
            hint="Mongo job documents currently being worked on."
          />
          <Card
            label="Completed"
            value={num(statusCounts.completed ?? 0)}
            hint="Finished migrations, all time."
          />
          <Card
            label="Failed"
            value={num(statusCounts.failed ?? 0)}
            tone={(statusCounts.failed ?? 0) > 0 ? "bad" : "ok"}
            hint="Jobs that gave up (e.g. stuck past the stale-sweep's retry budget). Re-run from the Storage Routing tab."
          />
        </div>
        {queue?.note && <p className="text-[11px] text-slate-500 mb-2">{queue.note}</p>}
        {(queue?.queued_jobs?.length ?? 0) > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-slate-500">
                <tr>
                  <Th hint="The job id as it sits in the Redis list, in dispatch order.">job id</Th>
                  <Th hint="Which storage domain this job is migrating.">domain</Th>
                  <Th hint="to_bunny_storage (S3 → Bunny) or to_s3 (Bunny → S3 cleanup).">direction</Th>
                  <Th hint="This job's real status, read from Mongo — being in this list is just dispatch signal, not status.">mongo status</Th>
                </tr>
              </thead>
              <tbody>
                {queue!.queued_jobs.map((j) => (
                  <tr key={j.job_id} className="border-t border-white/5">
                    <td className="py-1 font-mono text-slate-400">{j.job_id.slice(0, 12)}…</td>
                    <td className="text-slate-300">{j.domain ?? "—"}</td>
                    <td className="text-slate-400">{j.direction ?? "—"}</td>
                    <td className={STATUS_TONE[j.status ?? ""] ?? "text-slate-500"}>
                      {j.found_in_mongo ? j.status : "not found — stale id?"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {queue?.sample_truncated && (
              <p className="mt-1 text-[11px] text-amber-400">
                showing the first {queue.queued_jobs.length} of {queue.queue_length} queued ids
              </p>
            )}
          </div>
        )}
      </div>

      {/* Instance health */}
      <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4">
        <Card
          label="Memory used"
          value={m?.used_human ?? num(m?.used_bytes)}
          hint="Resident memory on this Redis. A genuinely separate container from REDIS_URL/REC_REDIS_URL (see docker-compose's redis-cdn service), so a spike here cannot starve either of those."
        />
        <Card
          label="Connections"
          value={`${num(c?.connected)} / ${num(cap || null)}`}
          tone={connPct > 70 ? "warn" : "ok"}
          hint="Live client connections vs. this container's own --maxclients. Isolated from REDIS_URL's and REC_REDIS_URL's containers, so raising this cap (or a migration burst using more of it) never affects either of those."
        />
        <Card
          label="Ops/sec"
          value={num(st?.ops_per_sec)}
          hint="Commands per second right now. This instance only ever sees RPUSH/BLPOP/LLEN from the migration queue, so any sustained non-zero rate outside an active migration is worth explaining."
        />
        <Card
          label="Rejected connections"
          value={num(st?.rejected_connections)}
          tone={(st?.rejected_connections ?? 0) > 0 ? "bad" : "ok"}
          hint="Connections this Redis refused outright. Non-zero means something is opening far more connections than the migration queue ever should."
        />
      </div>

      <div className="mb-4 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[11px] text-slate-500">
        This is the storage-migration queue's{" "}
        <span className="text-slate-200">own dedicated Redis container</span> (CDN_REDIS_URL) —
        a genuinely separate instance from REDIS_URL (main cache) and REC_REDIS_URL (rec_engine),
        not just another logical DB on either of theirs, so its connections, memory and
        connection cap are fully isolated from both. Configured pool size:{" "}
        <span className="text-slate-200">{num(ov.configured_pool_size ?? null)}</span>.
      </div>

      {/* Namespaces */}
      <div className="mb-4">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h4 className="text-sm font-semibold text-slate-200">Key namespaces</h4>
          <input
            value={match}
            onChange={(e) => setMatch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") loadNs(match); }}
            placeholder="SCAN pattern, e.g. storage_migration:*"
            className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs text-slate-200 w-64"
          />
          <button
            type="button"
            onClick={() => loadNs(match)}
            className="rounded-md border border-white/10 px-2 py-1 text-xs text-indigo-300 hover:bg-white/5"
          >
            Scan
          </button>
          {ns?.truncated && (
            <span className="text-[11px] text-amber-400">truncated at {num(ns.scanned)} keys</span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-slate-500">
              <tr>
                <Th className="py-1" hint="Key prefix up to the first colon. This instance should only ever show storage_migration:*.">namespace</Th>
                <Th hint="Keys counted under that prefix by the SCAN above.">keys</Th>
                <Th hint="Keys with no expiry set. The queue list itself has no TTL by design (it's a work queue, not a cache) — this only flags anything ELSE unexpectedly living here without one.">without TTL</Th>
              </tr>
            </thead>
            <tbody>
              {(ns?.namespaces ?? []).slice(0, 25).map((r) => (
                <tr key={r.namespace} className="border-t border-white/5">
                  <td className="py-1 font-mono text-slate-300">{r.namespace}</td>
                  <td className="text-slate-300">{num(r.keys)}</td>
                  <td className={r.keys_without_ttl > 0 ? "font-semibold text-amber-400" : "text-slate-500"}>
                    {num(r.keys_without_ttl)}
                  </td>
                </tr>
              ))}
              {(ns?.namespaces ?? []).length === 0 && (
                <tr><td colSpan={3} className="py-4 text-center text-slate-500">No keys found for this pattern right now.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Live connections */}
      <div>
        <h4 className="mb-2 text-sm font-semibold text-slate-200">
          Live connections — {num(cl?.total)} total
        </h4>
        <div className="max-h-72 overflow-x-auto overflow-y-auto">
          <table className="w-full text-left text-[11px]">
            <thead className="sticky top-0 bg-slate-900 text-slate-500">
              <tr>
                <Th className="py-1" hint="Client IP and port of the connection as Redis sees it.">addr</Th>
                <Th hint="The connection's CLIENT SETNAME value. This pool names itself cdn-migration-<hostname>, so an unnamed connection is something else — another service or a redis-cli session against this same DB.">name</Th>
                <Th hint="Seconds since this connection last ran a command. A worker's BLPOP shows as long idle by design while it waits for a job — that's not a problem.">idle</Th>
                <Th hint="The last command this connection issued.">cmd</Th>
                <Th hint="Plain-English reading of that last command.">what it's doing</Th>
              </tr>
            </thead>
            <tbody>
              {(cl?.clients ?? []).slice(0, 80).map((r, i) => (
                <tr key={`${r.addr}-${i}`} className="border-t border-white/5">
                  <td className="py-1 font-mono text-slate-400">{r.addr}</td>
                  <td className="font-mono text-slate-500">{r.name || "—"}</td>
                  <td className="text-slate-500">{r.idle_seconds}s</td>
                  <td className="text-slate-300">{r.cmd}</td>
                  <td className="text-slate-500">{r.cmd_explained}</td>
                </tr>
              ))}
              {(cl?.clients ?? []).length === 0 && (
                <tr><td colSpan={5} className="py-4 text-center text-slate-500">No live connections right now.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminCdnRedisHealthPage;
